<template>
  <div class="mobile-notice-add">
    <div class="add-container">
      <!-- 表单内容 -->
      <div class="form-content">
        <!-- 业务类型 -->
        <van-cell-group>
          <van-field
            v-model="form.bizTypeLabel"
            is-link
            readonly
            required
            label="业务类型"
            placeholder="请选择业务类型"
            @click="showBizTypePicker = true"
          />
          <van-popup v-model="showBizTypePicker" position="bottom" get-container="body">
            <van-picker
              show-toolbar
              :columns="bizTypeOptions"
              @cancel="showBizTypePicker = false"
              @confirm="onBizTypeConfirm"
            />
          </van-popup>
        </van-cell-group>

        <!-- 所属区域 - 社区（两列联动选择器，支持选择第一级或第二级） -->
        <van-cell-group v-if="form.bizType === 1">
          <van-field
            v-model="form.deptName"
            is-link
            readonly
            required
            label="所属社区"
            placeholder="请选择社区"
            @click="openDeptPicker"
          />
          <van-popup 
            v-model="showDeptPicker" 
            position="bottom" 
            get-container="body"
            :style="{ height: '55vh' }"
          >
            <div class="dept-picker-wrapper">
              <div class="picker-header">
                <span class="picker-title">选择所属区域</span>
                <span class="picker-close" @click="showDeptPicker = false">×</span>
              </div>
              <div class="picker-columns">
                <!-- 左列：街道列表（第一级） -->
                <div class="picker-column picker-column-left">
                  <div 
                    v-for="item in deptParentList" 
                    :key="item.value"
                    class="picker-item"
                    :class="{ active: selectedParent === item.value }"
                    @click="selectParent(item)"
                  >
                    {{ item.text }}
                    <span v-if="item.children && item.children.length > 0" class="item-count">
                      ({{ item.children.length }})
                    </span>
                  </div>
                  <div v-if="deptParentList.length === 0" class="picker-empty">
                    暂无数据
                  </div>
                </div>
                <!-- 右列：社区列表（第二级） -->
                <div class="picker-column picker-column-right">
                  <div 
                    v-for="item in deptChildList" 
                    :key="item.value"
                    class="picker-item"
                    :class="{ active: selectedChild === item.value }"
                    @click="selectChild(item)"
                  >
                    {{ item.text }}
                  </div>
                  <div v-if="deptChildList.length === 0" class="picker-empty">
                    {{ selectedParent ? '该街道暂无社区' : '请选择街道' }}
                  </div>
                </div>
              </div>
              <div class="picker-footer">
                <div class="picker-tip">
                  <span v-if="selectedParent && !selectedChild" class="tip-text">
                    已选择：{{ getSelectedParentName() }}
                  </span>
                  <span v-if="selectedChild" class="tip-text">
                    已选择：{{ getSelectedChildName() }}
                  </span>
                  <span v-if="!selectedParent && !selectedChild" class="tip-text">
                    请选择街道或社区
                  </span>
                </div>
                <div class="footer-buttons">
                  <van-button plain size="small" @click="showDeptPicker = false">取消</van-button>
                  <van-button 
                    type="primary" 
                    size="small" 
                    @click="confirmDept"
                    :disabled="!selectedParent && !selectedChild"
                  >
                    确认
                  </van-button>
                </div>
              </div>
            </div>
          </van-popup>
        </van-cell-group>

        <!-- 所属区域 - 园区 -->
        <van-cell-group v-if="form.bizType === 2">
          <van-field
            v-model="form.zoneParkName"
            is-link
            readonly
            required
            label="所属园区"
            placeholder="请选择园区"
            @click="showParkPicker = true"
          />
          <van-popup v-model="showParkPicker" position="bottom" get-container="body">
            <van-picker
              show-toolbar
              :columns="parkOptions"
              value-key="name"
              @cancel="showParkPicker = false"
              @confirm="onParkConfirm"
            />
          </van-popup>
        </van-cell-group>

        <!-- 标题 -->
        <van-cell-group>
          <van-field
            v-model="form.title"
            required
            label="标题"
            placeholder="请输入标题"
            maxlength="50"
          />
        </van-cell-group>

        <!-- 通知类型 -->
        <van-cell-group>
          <van-field
            v-model="form.notifyTypeLabel"
            is-link
            readonly
            required
            label="通知类型"
            placeholder="请选择通知类型"
            @click="showNotifyTypePicker = true"
          />
          <van-popup v-model="showNotifyTypePicker" position="bottom" get-container="body">
            <van-picker
              show-toolbar
              :columns="notifyTypeOptions"
              @cancel="showNotifyTypePicker = false"
              @confirm="onNotifyTypeConfirm"
            />
          </van-popup>
        </van-cell-group>

        <!-- 发布时间 -->
        <van-cell-group>
          <van-field
            v-model="form.publishTime"
            is-link
            readonly
            required
            label="发布时间"
            placeholder="请选择发布时间"
            @click="showPublishTimePicker = true"
          />
          <van-popup v-model="showPublishTimePicker" position="bottom" get-container="body">
            <van-datetime-picker
              v-model="currentDate"
              type="date"
              title="请选择发布时间"
              @cancel="showPublishTimePicker = false"
              @confirm="onPublishTimeConfirm"
            />
          </van-popup>
        </van-cell-group>

        <!-- 开始时间 -->
        <van-cell-group>
          <van-field
            v-model="form.startTime"
            is-link
            readonly
            required
            label="开始时间"
            placeholder="请选择开始时间"
            @click="showStartTimePicker = true"
          />
          <van-popup v-model="showStartTimePicker" position="bottom" get-container="body">
            <van-datetime-picker
              v-model="currentDate"
              type="date"
              title="请选择开始时间"
              @cancel="showStartTimePicker = false"
              @confirm="onStartTimeConfirm"
            />
          </van-popup>
        </van-cell-group>

        <!-- 结束时间 -->
        <van-cell-group>
          <van-field
            v-model="form.endTime"
            is-link
            readonly
            required
            label="结束时间"
            placeholder="请选择结束时间"
            @click="showEndTimePicker = true"
          />
          <van-popup v-model="showEndTimePicker" position="bottom" get-container="body">
            <van-datetime-picker
              v-model="currentDate"
              type="date"
              title="请选择结束时间"
              @cancel="showEndTimePicker = false"
              @confirm="onEndTimeConfirm"
            />
          </van-popup>
        </van-cell-group>

        <!-- 提示次数 -->
        <van-cell-group>
          <van-field
            v-model="form.notifyTimesPerUser"
            required
            label="提示次数"
            placeholder="请输入提示次数"
            type="digit"
            maxlength="2"
          />
        </van-cell-group>

        <!-- 描述内容 - 使用富文本编辑器 -->
        <div class="editor-wrapper">
          <div class="editor-label">描述内容 <span class="required-mark">*</span></div>
          <Editor :value.sync="form.description" placeholder="请输入描述内容" />
        </div>
      </div>

      <!-- 提交按钮 -->
      <div class="submit-wrapper">
        <van-button
          type="info"
          size="large"
          round
          :loading="submitting"
          @click="submitForm"
        >
          {{ submitting ? '提交中...' : '发 布' }}
        </van-button>
      </div>
    </div>
  </div>
</template>

<script>
import { Toast } from 'vant';
import { addbusinessInfo, updatebusinessInfo, listZonePark, deptTreeSelect } from "@/api/Changan-zhsq/businessInfo";
import Editor from "@/components/Editor";
import { setwgToken } from '@/utils/auth'

export default {
  name: 'MobileNoticeAdd',
  components: {
    Editor
  },
  data() {
    return {
      title: '新增公告',
      submitting: false,
      currentDate: new Date(),
      
      // 业务类型
      showBizTypePicker: false,
      bizTypeOptions: [
        { text: '社区', value: 1 },
        { text: '园区', value: 2 }
      ],
      
      // 社区相关（两列联动选择器）
      showDeptPicker: false,
      deptParentList: [], // 街道列表（第一级）
      deptChildList: [], // 社区列表（第二级）
      selectedParent: null, // 选中的街道ID
      selectedChild: null, // 选中的社区ID
      
      // 园区相关
      showParkPicker: false,
      parkOptions: [],
      
      // 通知类型
      showNotifyTypePicker: false,
      notifyTypeOptions: [
        { text: '弹窗', value: 1 }
      ],
      
      // 时间选择器
      showPublishTimePicker: false,
      showStartTimePicker: false,
      showEndTimePicker: false,
      
      // 表单数据
      form: {
        id: null,
        bizType: null,
        bizTypeLabel: '',
        deptId: null,
        deptName: '',
        zoneParkId: null,
        zoneParkName: '',
        title: '',
        description: '',
        publishTime: '',
        startTime: '',
        endTime: '',
        notifyType: null,
        notifyTypeLabel: '',
        notifyTimesPerUser: null,
      }
    };
  },
  created() {
    // 判断是新增还是修改
    const data = this.$route.query.data;
    if (data) {
      try {
        const row = JSON.parse(data);
        this.form = {
          ...this.form,
          ...row,
          bizTypeLabel: row.bizType === 1 ? '社区' : row.bizType === 2 ? '园区' : '',
          notifyTypeLabel: row.notifyType === 1 ? '弹窗' : '',
        };
        this.title = '修改公告';
      } catch (e) {
        console.error('解析数据失败', e);
      }
    }
    setwgToken(this.$route.query.tok);
    this.getDeptTree();
    this.getParkList();
  },
  methods: {
    /** 获取社区树 */
    getDeptTree() {
      deptTreeSelect().then(response => {
        const data = response.data.data || [];
        // 构建街道列表（第一级）
        this.deptParentList = data.map(item => ({
          ...item,
          text: item.organName || item.OrganName,
          value: item.organCode || item.OrganCode,
          children: (item.children || item.Children || []).map(child => ({
            ...child,
            text: child.organName || child.OrganName,
            value: child.organCode || child.OrganCode
          }))
        }));
        
        // 如果是修改，回显选中的值
        if (this.form.deptId) {
          this.initDeptSelection();
        }
      }).catch(() => {
        // 模拟数据（用于测试）
        this.deptParentList = [
          {
            text: '长安路街道',
            value: 410308005,
            children: [
              { text: '青岛路一社区', value: 410308005001 },
              { text: '青岛路二社区', value: 410308005002 }
            ]
          },
          {
            text: '南昌路街道',
            value: 410308006,
            children: [
              { text: '南昌路一社区', value: 410308006001 },
              { text: '南昌路二社区', value: 410308006002 }
            ]
          }
        ];
        if (this.form.deptId) {
          this.initDeptSelection();
        }
      });
    },
    
    /** 初始化选择（用于回显） */
    initDeptSelection() {
      // 先检查是否选中了街道
      const parent = this.deptParentList.find(item => item.value === this.form.deptId);
      if (parent) {
        this.selectedParent = parent.value;
        this.selectedChild = null;
        this.deptChildList = parent.children || [];
        return;
      }
      
      // 再检查是否选中了社区
      for (const p of this.deptParentList) {
        const child = (p.children || []).find(c => c.value === this.form.deptId);
        if (child) {
          this.selectedParent = p.value;
          this.selectedChild = child.value;
          this.deptChildList = p.children || [];
          return;
        }
      }
    },
    
    /** 打开社区选择器 */
    openDeptPicker() {
      // 如果有选中的街道，自动加载对应的社区列表
      if (this.selectedParent) {
        const parent = this.deptParentList.find(item => item.value === this.selectedParent);
        if (parent) {
          this.deptChildList = parent.children || [];
        }
      }
      this.showDeptPicker = true;
    },
    
    /** 选择街道（第一级） */
    selectParent(item) {
      if (this.selectedParent === item.value) {
        // 如果点击的是已经选中的街道，取消选中
        this.selectedParent = null;
        this.selectedChild = null;
        this.deptChildList = [];
      } else {
        this.selectedParent = item.value;
        this.selectedChild = null;
        // 获取该街道下的社区列表
        this.deptChildList = item.children || [];
      }
    },
    
    /** 选择社区（第二级） */
    selectChild(item) {
      if (this.selectedChild === item.value) {
        // 如果点击的是已经选中的社区，取消选中
        this.selectedChild = null;
      } else {
        this.selectedChild = item.value;
        // 确保父级也被选中
        if (!this.selectedParent) {
          // 如果父级未选中，自动选中对应的父级
          for (const p of this.deptParentList) {
            if ((p.children || []).some(c => c.value === item.value)) {
              this.selectedParent = p.value;
              this.deptChildList = p.children || [];
              break;
            }
          }
        }
      }
    },
    
    /** 获取选中的街道名称 */
    getSelectedParentName() {
      const parent = this.deptParentList.find(item => item.value === this.selectedParent);
      return parent ? parent.text : '';
    },
    
    /** 获取选中的社区名称 */
    getSelectedChildName() {
      if (!this.selectedChild) return '';
      for (const p of this.deptParentList) {
        const child = (p.children || []).find(c => c.value === this.selectedChild);
        if (child) return child.text;
      }
      return '';
    },
    
    /** 确认选择 */
    confirmDept() {
      if (!this.selectedParent && !this.selectedChild) {
        Toast.fail('请选择街道或社区');
        return;
      }
      
      // 如果选中的是社区
      if (this.selectedChild) {
        const child = this.deptChildList.find(item => item.value === this.selectedChild);
        if (child) {
          this.form.deptId = child.value;
          this.form.deptName = child.text;
        }
      } 
      // 如果选中的是街道（没有选社区）
      else if (this.selectedParent) {
        const parent = this.deptParentList.find(item => item.value === this.selectedParent);
        if (parent) {
          this.form.deptId = parent.value;
          this.form.deptName = parent.text;
        }
      }
      
      this.showDeptPicker = false;
    },
    
    /** 获取园区列表 */
    getParkList() {
      listZonePark().then(response => {
        this.parkOptions = response.data.data || [];
      }).catch(() => {
        // 模拟数据
        this.parkOptions = [
          { id: 1, name: '科技园区' },
          { id: 2, name: '创新园区' }
        ];
      });
    },
    
    /** 业务类型确认 */
    onBizTypeConfirm(value) {
      this.form.bizType = value.value;
      this.form.bizTypeLabel = value.text;
      // 清空对应的区域选择
      if (value.value === 1) {
        this.form.zoneParkId = null;
        this.form.zoneParkName = '';
        // 重置社区选择状态
        this.selectedParent = null;
        this.selectedChild = null;
        this.deptChildList = [];
      } else {
        this.form.deptId = null;
        this.form.deptName = '';
      }
      this.showBizTypePicker = false;
    },
    
    /** 园区确认 */
    onParkConfirm(value) {
      this.form.zoneParkId = value.id;
      this.form.zoneParkName = value.name;
      this.showParkPicker = false;
    },
    
    /** 通知类型确认 */
    onNotifyTypeConfirm(value) {
      this.form.notifyType = value.value;
      this.form.notifyTypeLabel = value.text;
      this.showNotifyTypePicker = false;
    },
    
    /** 发布时间确认 */
    onPublishTimeConfirm(value) {
      this.form.publishTime = this.formatDate(value);
      this.showPublishTimePicker = false;
    },
    
    /** 开始时间确认 */
    onStartTimeConfirm(value) {
      this.form.startTime = this.formatDate(value);
      this.showStartTimePicker = false;
    },
    
    /** 结束时间确认 */
    onEndTimeConfirm(value) {
      this.form.endTime = this.formatDate(value);
      this.showEndTimePicker = false;
    },
    
    /** 格式化日期 */
    formatDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    
    /** 表单验证 */
    validateForm() {
      if (!this.form.bizType) {
        Toast.fail('请选择业务类型');
        return false;
      }
      if (this.form.bizType === 1 && !this.form.deptId) {
        Toast.fail('请选择所属社区');
        return false;
      }
      if (this.form.bizType === 2 && !this.form.zoneParkId) {
        Toast.fail('请选择所属园区');
        return false;
      }
      if (!this.form.title || !this.form.title.trim()) {
        Toast.fail('请输入标题');
        return false;
      }
      if (!this.form.notifyType) {
        Toast.fail('请选择通知类型');
        return false;
      }
      if (!this.form.publishTime) {
        Toast.fail('请选择发布时间');
        return false;
      }
      if (!this.form.startTime) {
        Toast.fail('请选择开始时间');
        return false;
      }
      if (!this.form.endTime) {
        Toast.fail('请选择结束时间');
        return false;
      }
      if (!this.form.notifyTimesPerUser) {
        Toast.fail('请输入提示次数');
        return false;
      }
      if (this.form.notifyTimesPerUser < 1 || this.form.notifyTimesPerUser > 99) {
        Toast.fail('提示次数范围为1-99');
        return false;
      }
      if (!this.form.description || !this.form.description.trim()) {
        Toast.fail('请输入描述内容');
        return false;
      }
      return true;
    },
    
    /** 提交表单 */
    submitForm() {
      if (!this.validateForm()) {
        return;
      }
      
      this.submitting = true;
      
      const submitData = {
        ...this.form,
        notifyTimesPerUser: Number(this.form.notifyTimesPerUser)
      };
      
      // 移除显示用的字段
      delete submitData.bizTypeLabel;
      delete submitData.notifyTypeLabel;
      delete submitData.zoneParkName;
      
      const apiCall = this.form.id 
        ? updatebusinessInfo(submitData) 
        : addbusinessInfo(submitData);
      
      apiCall.then(() => {
        Toast.success(this.form.id ? '修改成功' : '发布成功');
        this.submitting = false;
        setTimeout(() => {
          this.$router.go(-1);
        }, 3000);
      }).catch((err) => {
        this.submitting = false;
        Toast.fail(err.msg || '操作失败，请重试');
      });
    }
  }
};
</script>

<style scoped>
.mobile-notice-add {
  min-height: 100vh;
  background: #f5f7fa;
  padding: 12px 16px 100px;
  width: calc(100% - 32px);
}

.add-container {
  max-width: 480px;
  margin: 0 auto;
}

.page-header {
  padding: 16px 0 20px;
  text-align: center;
}

.header-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
}

.form-content {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

/* 覆盖 Vant 样式 */
.van-cell-group {
  margin: 0;
}

.van-cell {
  padding: 14px 16px;
  font-size: 14px;
}

.van-cell::after {
  border-bottom: 1px solid #f0f0f0;
}

.van-field__label {
  width: 80px;
  color: #333;
}

.van-field__body {
  color: #666;
}

.van-field__control {
  text-align: right;
}

.van-field__control::placeholder {
  color: #bbb;
}

.van-field__right-icon {
  color: #bbb;
}

/* 编辑器样式 */
.editor-wrapper {
  padding: 12px 0 20px;
  background: #fff;
}

.editor-label {
  padding: 0 16px 10px;
  font-size: 14px;
  color: #333;
}

.required-mark {
  color: #ee0a24;
  margin-left: 2px;
}

/* 提交按钮 */
.submit-wrapper {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px 20px 20px;
  background: #fff;
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.06);
  z-index: 100;
}

.submit-wrapper .van-button {
  background: linear-gradient(135deg, #116efa, #0a5cdb);
  border: none;
  font-size: 16px;
}

.submit-wrapper .van-button--large {
  height: 48px;
}

/* 两列联动选择器样式 */
.dept-picker-wrapper {
  background: #fff;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.picker-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.picker-title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.picker-close {
  font-size: 24px;
  color: #999;
  cursor: pointer;
  line-height: 1;
  padding: 0 4px;
}

.picker-columns {
  flex: 1;
  display: flex;
  overflow: hidden;
  min-height: 0;
}

.picker-column {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
  -webkit-overflow-scrolling: touch;
}

.picker-column-left {
  border-right: 1px solid #f0f0f0;
  background: #fafafa;
}

.picker-column-right {
  background: #fff;
}

.picker-item {
  padding: 14px 16px;
  text-align: center;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
}

.picker-item:active {
  background: #f5f5f5;
}

.picker-item.active {
  color: #116efa;
  background: #f0f7ff;
  font-weight: 500;
}

.picker-item.active::after {
  content: '✓';
  position: absolute;
  right: 16px;
  color: #116efa;
  font-weight: 600;
  font-size: 16px;
}

.item-count {
  font-size: 12px;
  color: #999;
  font-weight: 400;
}

.picker-empty {
  padding: 40px 16px;
  text-align: center;
  font-size: 14px;
  color: #bbb;
}

.picker-footer {
  padding: 12px 16px;
  border-top: 1px solid #f0f0f0;
  flex-shrink: 0;
  background: #fff;
}

.picker-tip {
  text-align: center;
  margin-bottom: 10px;
  min-height: 20px;
}

.tip-text {
  font-size: 14px;
  color: #666;
}

.tip-text:not(:empty)::before {
  content: '当前选择: ';
  color: #999;
}

.footer-buttons {
  display: flex;
  gap: 12px;
}

.footer-buttons .van-button {
  flex: 1;
  height: 40px;
  border-radius: 20px;
}

.footer-buttons .van-button--primary {
  background: linear-gradient(135deg, #116efa, #0a5cdb);
  border: none;
}

.footer-buttons .van-button--primary[disabled] {
  background: #ccc;
  opacity: 0.6;
}

/* Picker 弹出层样式 */
.van-picker__toolbar {
  padding: 12px 16px;
}

.van-picker__cancel,
.van-picker__confirm {
  font-size: 14px;
}

.van-picker__title {
  font-size: 16px;
  font-weight: 500;
}
</style>